"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, Snowflake, Sun, CloudRain, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const challenges = [{
  id: 1,
  title: "Extreme Seasonality & Volatility",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Manage sharp peak holiday surges against prolonged monsoon and shoulder dips. We build dynamic minimum length-of-stay controls and pace-based pricing so your Manali property never relies on panic discounting.",
  metricLabel: "Strategy focus",
  metricValue: "Pace-based pacing & minimum stay restrictions"
}, {
  id: 2,
  title: "Aggressive Comp-Set Undercutting",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Track intraday rate shifts across MakeMyTrip, Agoda, and Booking.com. Automated competitive alerts and yield adjustments protect your RevPAR while securing high-intent mountain travelers ahead of market drops.",
  metricLabel: "Strategy focus",
  metricValue: "Intraday OTA monitoring & rule-based rate fencing"
}, {
  id: 3,
  title: "The OTA Commission & Parity Trap",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Stop unauthorized B2B wholesaler leakage and reclaim margins lost to steep OTA commissions. We enforce strict rate parity across meta-search and drive direct guest conversions for high-value cottages and suites.",
  metricLabel: "Strategy focus",
  metricValue: "Wholesaler rate fencing & direct channel boost"
}];
const seasonalitySeasons = [{
  season: "Summer Peak (May – June)",
  icon: Sun,
  badge: "Maximum Yield",
  desc: "Family leisure, honeymooners & escape travelers. Premium pricing, strict 2-3 night MLOS, and dynamic surge fencing.",
  tagColor: "text-amber-600 dark:text-amber-400 bg-amber-500/10 border-amber-500/20"
}, {
  season: "Winter Snow Rush (Dec – Jan)",
  icon: Snowflake,
  badge: "High ADR",
  desc: "Snowfall seekers, Christmas & New Year celebrations. High-velocity booking windows yielding premium rates on heated luxury inventory.",
  tagColor: "text-sky-600 dark:text-sky-400 bg-sky-500/10 border-sky-500/20"
}, {
  season: "Monsoon & Low Demand (July – Sept)",
  icon: CloudRain,
  badge: "Occupancy Cushion",
  desc: "Weather-induced lulls. Focus on extended workation stays, corporate off-sites, and targeted value-add packages without rate collapse.",
  tagColor: "text-slate-600 dark:text-slate-400 bg-slate-500/10 border-slate-500/20"
}, {
  season: "Autumn / Spring Shoulder (Feb–Apr & Oct–Nov)",
  icon: Sparkles,
  badge: "Shoulder Optimization",
  desc: "Adventure travelers, trekking enthusiasts & wellness groups. Dynamic shoulder packages to sustain healthy base ADR.",
  tagColor: "text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border-emerald-500/20"
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapemanali" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Manali
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Manali presents a dynamic mountain destination micro-market with compressed high-demand seasons (summer snow escapes and winter holiday peaks), weather-dependent booking windows, and intense OTA competition. Sunil Singh structures disciplined revenue models designed specifically for Himalayan resorts, boutique cottages, and luxury hillside stays.
          </p>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Seasonality Strategy Block */}
        <div className="mt-14 md:mt-20 rounded-xl border border-border bg-card p-6 md:p-8 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6 border-b border-border/70 pb-5">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                Himalayan Demand Cycles
              </span>
              <h3 className="font-serif text-2xl font-bold text-foreground mt-1">
                Manali Seasonality & Yield Distribution
              </h3>
            </div>
            <p className="text-xs text-muted-foreground max-w-md">
              Custom rate fences, pace triggers, and booking rules configured for each climatic and tourism cycle.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {seasonalitySeasons.map((seasonItem, i) => {
            const SeasonIcon = seasonItem.icon;
            return <div key={seasonItem.season} data-index={i} className="rounded-lg border border-border/70 bg-background/60 p-4.5 flex flex-col justify-between hover:border-secondary/50 transition-[border-color,box-shadow] duration-200">
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="inline-flex size-8 items-center justify-center rounded-md bg-secondary/15 text-secondary">
                        <SeasonIcon className="size-4" />
                      </div>
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded border ${seasonItem.tagColor}`}>
                        {seasonItem.badge}
                      </span>
                    </div>
                    <h4 className="font-semibold text-sm text-foreground mb-2">
                      {seasonItem.season}
                    </h4>
                    <p className="text-xs leading-relaxed text-muted-foreground">
                      {seasonItem.desc}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Manali hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}